import React, { useState, useEffect } from 'react';
import { Databases, Query } from "appwrite";
import client from '../app-user';
import { FaAngleDown, FaAngleLeft, FaAngleRight } from 'react-icons/fa';
import LoadingSpinner from '../components/LoadingSpinner';
import { DEFAULT_SALARY_CAP, getLeagueSettings } from '../services/leagueSettings';
import styles from '../styles/sheets.module.css';

const databases = new Databases(client);

// Define a helper function to check and log if default values are being used
function getEnvVariable(name: string, defaultValue: string) {
  const value = process.env[name];
  if (value === undefined) {
    console.warn(`Environment variable ${name} is not set. Using default value: ${defaultValue}`);
    return defaultValue;
  }
  return value;
}

// Use the helper function to initialize your constants
const DATABASE_ID = getEnvVariable('REACT_APP_DATABASE_ID', 'database');
const ROSTERED_PLAYER_DATA_COLLECTION_ID = getEnvVariable('REACT_APP_ROSTERED_PLAYER_DATA_COLLECTION_ID', 'roster data');
const DEAD_CAP_DATA_COLLECTION_ID = getEnvVariable('REACT_APP_DEAD_CAP_DATA_COLLECTION_ID', 'dead cap');
const TEAMS_COLLECTION_ID = getEnvVariable('REACT_APP_TEAMS_COLLECTION_ID', 'teams');

interface PlayerDocument {
  roster_id: string;
  roster_status: 'starter' | 'bench' | 'reserve' | 'taxi'; // Adjust as needed
  owner: string;
  team_name: string | null;
  playerId: string;
  name: string;
  first_name: string;
  last_name: string;
  position: string;
  team: string;
  college: string;
  number: number;
  age: number;
  fantasy_positions: string[]; // Adjust the type as needed
  salary: number;
  duration: number;
  contract_type: string;
}

interface CapPenaltyDocument {
  salary: number;
  duration: number;
  roster_id: string;
  name: string;
  playerId: string;
}

interface TeamDocument {
  name: string;
  sleeperID?: string;
}

interface SleeperUser {
  avatar?: string | null;
}

// Define TypeScript interfaces
interface Player {
  playerId: string;
  name: string;
  position: string;
  fantasy_positions: string[];
  team: string;
  number: number;
  status: 'starter' | 'bench' | 'reserve' | 'taxi';
  salary: number;
  duration: number;
  contract_type: string;
}

interface CutPlayer {
  salary: number;
  duration: number;
  roster_id: string;
  name: string;
  playerId: string;
}

interface Team {
  id: string;
  team_name: string;
  owner: string;
  userId?: string;
  avatarId?: string | null;
  roster: Player[];
  cutPlayer: CutPlayer[];
}

type ContractAmountFilter = 'all' | 'zero' | '1-5' | '6-10' | '11-20' | '21-plus';

// Your Appwrite API calls would go here
const fetchData = async () => {
  try {
    let allDocuments: PlayerDocument[] = [];
    let capDocuments: CapPenaltyDocument[] = [];
    let teamDocuments: TeamDocument[] = [];

    let limit = 50;
    let offset = 0;

    while (true) {
      const result = await databases.listDocuments(
        DATABASE_ID, 
        ROSTERED_PLAYER_DATA_COLLECTION_ID, 
        [Query.limit(limit),
          Query.offset(offset)],
      );

      const documents = result.documents as unknown as PlayerDocument[];

      allDocuments = allDocuments.concat(documents);

      if (result.documents.length < limit) {
        // Break the loop if the number of documents is less than the limit,
        // indicating that we've reached the end of the collection
        break;
      }

      offset += limit;
    }

    offset = 0; // Reset offset for dead cap data fetching

    while (true) {
      const result = await databases.listDocuments(
        DATABASE_ID, 
        DEAD_CAP_DATA_COLLECTION_ID, 
        [Query.limit(limit),
          Query.offset(offset)],
      );

      const documents = result.documents as unknown as CapPenaltyDocument[];

      capDocuments = capDocuments.concat(documents);

      if (result.documents.length < limit) {
        // Break the loop if the number of documents is less than the limit,
        // indicating that we've reached the end of the collection
        break;
      }

      offset += limit;
    }

    offset = 0;

    while (true) {
      const result = await databases.listDocuments(
        DATABASE_ID,
        TEAMS_COLLECTION_ID,
        [Query.limit(limit), Query.offset(offset)],
      );

      const documents = result.documents as unknown as TeamDocument[];
      teamDocuments = teamDocuments.concat(documents);

      if (result.documents.length < limit) {
        break;
      }

      offset += limit;
    }

    const playerDocuments = allDocuments as unknown as PlayerDocument[]
    const normalize = (value?: string | null) => (value || '').trim().toLowerCase();
    const getTeamName = (teamName: string | null, owner: string) => {
      const customName = teamName?.trim();
      return customName && customName.toLowerCase() !== 'null' ? customName : owner;
    };

    // Using TypeScript's Record type for the accumulator
    const teams = playerDocuments.reduce<Record<string, Team>>((acc, playerDoc) => {
      const teamId = playerDoc.roster_id;
      const teamName = getTeamName(playerDoc.team_name, playerDoc.owner);

      if (!acc[teamId]) {
        const matchingTeam = teamDocuments.find(team =>
          normalize(team.name) === normalize(playerDoc.owner) ||
          normalize(team.name) === normalize(teamName) ||
          normalize(team.sleeperID) === normalize(playerDoc.owner)
        );

        acc[teamId] = {
          id: teamId,
          team_name: teamName,
          owner: matchingTeam?.name || playerDoc.owner,
          userId: matchingTeam?.sleeperID,
          avatarId: null,
          roster: [],
          cutPlayer: []
        };
      } else if (playerDoc.team_name?.trim()) {
        acc[teamId].team_name = teamName;
      }

      const player: Player = {
        playerId: playerDoc.playerId,
        name: playerDoc.name,
        position: playerDoc.position,
        fantasy_positions: playerDoc.fantasy_positions,
        team: playerDoc.team,
        number: playerDoc.number,
        status: playerDoc.roster_status,
        salary: playerDoc.salary,
        duration: playerDoc.duration,
        contract_type: playerDoc.contract_type
      };

      acc[teamId].roster.push(player);

      return acc;
    }, {});

    // Add dead cap data to teams
    capDocuments.forEach(capDoc => {
      const teamId = capDoc.roster_id;
      if (teams[teamId]) {
        const deadCapPlayer: CutPlayer = {
          playerId: capDoc.playerId,
          roster_id: capDoc.roster_id,
          name: capDoc.name,
          salary: capDoc.salary,
          duration: capDoc.duration
        };
        teams[teamId].cutPlayer.push(deadCapPlayer);
      }
    });

    const teamsWithAvatars = await Promise.all(Object.values(teams).map(async team => {
      if (!team.userId) return team;

      try {
        const response = await fetch(`https://api.sleeper.app/v1/user/${encodeURIComponent(team.userId)}`);
        if (!response.ok) return team;

        const sleeperUser = await response.json() as SleeperUser;
        return { ...team, avatarId: sleeperUser.avatar || null };
      } catch (error) {
        console.warn(`Unable to load avatar for ${team.team_name}:`, error);
        return team;
      }
    }));

    return teamsWithAvatars;
  } catch (error) {
    console.error('Error fetching data:', error);
    return []; // Return an empty array in case of error
  }
};

const TeamAvatar: React.FC<{ team: Team; size?: 'small' | 'large' }> = ({ team, size = 'small' }) => {
  const initials = team.team_name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(word => word[0])
    .join('')
    .toUpperCase();

  return (
    <span className={`${styles.teamAvatar} ${size === 'large' ? styles.teamAvatarLarge : ''}`} aria-hidden="true">
      {team.avatarId ? (
        <img
          src={`https://sleepercdn.com/avatars/thumbs/${team.avatarId}`}
          alt=""
        />
      ) : (
        <span className={styles.avatarFallback}>{initials || '?'}</span>
      )}
    </span>
  );
};

// Main component
const TeamRosters: React.FC = () => {
  const [teams, setTeams] = useState<Team[]>([]);
  const [selectedTeamId, setSelectedTeamId] = useState<string>('');
  const [isTeamMenuOpen, setIsTeamMenuOpen] = useState(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [salaryCap, setSalaryCap] = useState(DEFAULT_SALARY_CAP);
  const [positionFilter, setPositionFilter] = useState('all');
  const [contractAmountFilter, setContractAmountFilter] = useState<ContractAmountFilter>('all');
  const [durationFilter, setDurationFilter] = useState('all');
  const [contractTypeFilter, setContractTypeFilter] = useState('all');

  useEffect(() => {
    const fetchAndSetData = async () => {
      setIsLoading(true); // Start loading
      const data = await fetchData();
      setTeams(data);
      setSelectedTeamId(current => current || data[0]?.id || '');
      setIsLoading(false); // End loading
    };
    
    fetchAndSetData();
  }, []);

  useEffect(() => {
    getLeagueSettings().then((settings) => setSalaryCap(settings.salaryCap));
  }, []);

  const resetFilters = () => {
    setPositionFilter('all');
    setContractAmountFilter('all');
    setDurationFilter('all');
    setContractTypeFilter('all');
  };

  const selectTeam = (teamId: string) => {
    setSelectedTeamId(teamId);
    setIsTeamMenuOpen(false);
    resetFilters();
  };

  const cycleTeam = (direction: -1 | 1) => {
    if (teams.length === 0) return;

    const currentIndex = teams.findIndex(team => team.id === selectedTeamId);
    const nextIndex = (Math.max(currentIndex, 0) + direction + teams.length) % teams.length;
    setSelectedTeamId(teams[nextIndex].id);
    setIsTeamMenuOpen(false);
    resetFilters();
  };

  // Loading indicator rendering
  if (isLoading) {
    return <LoadingSpinner />;
  }

  const matchesContractAmount = (salary: number) => {
    const amount = Number(salary) || 0;
    if (contractAmountFilter === 'zero') return amount === 0;
    if (contractAmountFilter === '1-5') return amount >= 1 && amount <= 5;
    if (contractAmountFilter === '6-10') return amount >= 6 && amount <= 10;
    if (contractAmountFilter === '11-20') return amount >= 11 && amount <= 20;
    if (contractAmountFilter === '21-plus') return amount >= 21;
    return true;
  };

  const matchesDuration = (duration: number) => durationFilter === 'all' || String(duration) === durationFilter;

  const getRosterPlayers = (team: Team, status: Player['status']) => {
    const positionOrder = ['QB', 'RB', 'WR', 'TE', 'NT', 'DT', 'DE', 'DL', 'LB', 'CB', 'FS', 'SS', 'DB', 'K'];

    return team.roster
      .filter(player => player.status === status)
      .filter(player => {
        const fantasyPositions = player.fantasy_positions?.length ? player.fantasy_positions : [player.position];
        const matchesPosition = positionFilter === 'all' || fantasyPositions.includes(positionFilter);
        const matchesType = contractTypeFilter === 'all' || player.contract_type === contractTypeFilter;
        return matchesPosition && matchesContractAmount(player.salary) && matchesDuration(player.duration) && matchesType;
      })
      .sort((a, b) => positionOrder.indexOf(a.position) - positionOrder.indexOf(b.position));
  };

  const renderRosterSection = (players: Player[]) => players.map((player) => {
      const fantasyPositions = player.fantasy_positions?.length
        ? player.fantasy_positions.join('/')
        : player.position;

      return (
        <article className={styles.playerCard} key={player.playerId}>
          <img
            src={`https://sleepercdn.com/content/nfl/players/thumb/${player.playerId}.jpg`}
            className={styles.playerPic}
            alt={`${player.name} headshot`}
          />
          <div className={styles.playerIdentity}>
            <span className={styles.name}>{player.name}</span>
            <span className={styles.mobilePlayerInfo}>
              {fantasyPositions} - {player.number ? `#${player.number}` : '#—'} - {player.team || 'Free agent'}
            </span>
          </div>
          <span className={styles.tableCell}>{fantasyPositions}</span>
          <span className={styles.tableCell}>{player.number ? `#${player.number}` : '#—'}</span>
          <span className={styles.tableCell}>{player.team || 'Free agent'}</span>
          <div className={styles.contract}>
            <strong className={styles.amount}>${player.salary}</strong>
            <span className={styles.duration}>{player.duration} {player.duration === 1 ? 'year' : 'years'}</span>
            <span className={styles.type}>{player.contract_type || '—'}</span>
          </div>
        </article>
      );
    });

  const getDeadMoneyPlayers = (team: Team) => team.cutPlayer.filter(player => (
    positionFilter === 'all' &&
    matchesContractAmount(player.salary) &&
    matchesDuration(player.duration) &&
    (contractTypeFilter === 'all' || contractTypeFilter === 'dead-money')
  ));

  const renderDeadMoneySection = (players: CutPlayer[]) => players.map((player) => (
      <article className={styles.playerCard} key={player.playerId}>
        <img
          src={`https://sleepercdn.com/content/nfl/players/thumb/${player.playerId}.jpg`}
          className={styles.playerPic}
          alt={`${player.name} headshot`}
        />
        <div className={styles.playerIdentity}>
          <span className={styles.name}>{player.name}</span>
          <span className={styles.mobilePlayerInfo}>Dead money charge</span>
        </div>
        <span className={styles.tableCell}>—</span>
        <span className={styles.tableCell}>—</span>
        <span className={styles.tableCell}>—</span>
        <div className={styles.contract}>
          <strong className={styles.amount}>${player.salary}</strong>
          <span className={styles.duration}>{player.duration} {player.duration === 1 ? 'year' : 'years'}</span>
          <span className={styles.type}>Terminated</span>
        </div>
      </article>
    ));

  const selectedTeamData = teams.find(team => team.id === selectedTeamId);
  const positionOptions = selectedTeamData
    ? Array.from(new Set(selectedTeamData.roster.flatMap(player => (
      player.fantasy_positions?.length ? player.fantasy_positions : [player.position]
    )).filter(Boolean))).sort()
    : [];
  const durationOptions = selectedTeamData
    ? Array.from(new Set([
      ...selectedTeamData.roster.map(player => player.duration),
      ...selectedTeamData.cutPlayer.map(player => player.duration),
    ])).sort((a, b) => a - b)
    : [];
  const rosterSections = selectedTeamData ? [
    { id: 'starters', title: 'Starters', players: getRosterPlayers(selectedTeamData, 'starter') },
    { id: 'bench', title: 'Bench', players: getRosterPlayers(selectedTeamData, 'bench') },
    { id: 'injured-reserve', title: 'Injured Reserve', players: getRosterPlayers(selectedTeamData, 'reserve') },
    { id: 'taxi-squad', title: 'Taxi Squad', players: getRosterPlayers(selectedTeamData, 'taxi') },
  ] : [];
  const deadMoneyPlayers = selectedTeamData ? getDeadMoneyPlayers(selectedTeamData) : [];
  const hasActiveFilters = positionFilter !== 'all' || contractAmountFilter !== 'all' ||
    durationFilter !== 'all' || contractTypeFilter !== 'all';
  const activeCap = selectedTeamData
    ? selectedTeamData.roster.reduce((total, player) => total + (Number(player.salary) || 0), 0)
    : 0;
  const deadCap = selectedTeamData
    ? selectedTeamData.cutPlayer.reduce((total, player) => total + (Number(player.salary) || 0), 0)
    : 0;
  const usedCap = activeCap + deadCap;
  const remainingCap = salaryCap - usedCap;
  const activeCapPercent = salaryCap > 0 ? Math.min(100, Math.max(0, (activeCap / salaryCap) * 100)) : 0;
  const deadCapPercent = salaryCap > 0
    ? Math.min(100 - activeCapPercent, Math.max(0, (deadCap / salaryCap) * 100))
    : 0;
  const formatCap = (value: number) => `${value < 0 ? '-' : ''}$${Math.abs(value).toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}`;

  return (
    <div className={styles.background}>
      {selectedTeamData && (
        <section className={styles.teamNavigation} aria-label="Team selector">
          <div className={styles.teamSwitcher}>
            <button
              type="button"
              className={styles.cycleButton}
              onClick={() => cycleTeam(-1)}
              aria-label="Previous team"
            >
              <FaAngleLeft aria-hidden="true" />
            </button>

            <button
              type="button"
              className={styles.currentTeamButton}
              onClick={() => setIsTeamMenuOpen(open => !open)}
              aria-expanded={isTeamMenuOpen}
              aria-controls="team-menu"
            >
              <TeamAvatar team={selectedTeamData} size="large" />
              <span className={styles.currentTeamName}>{selectedTeamData.team_name}</span>
              <span className={`${styles.menuCaret} ${isTeamMenuOpen ? styles.menuCaretOpen : ''}`} aria-hidden="true">
                <FaAngleDown />
              </span>
            </button>

            <button
              type="button"
              className={styles.cycleButton}
              onClick={() => cycleTeam(1)}
              aria-label="Next team"
            >
              <FaAngleRight aria-hidden="true" />
            </button>
          </div>

          {isTeamMenuOpen && (
            <div className={styles.teamMenu} id="team-menu">
              <div className={styles.teamGrid}>
                {teams.map(team => (
                  <button
                    type="button"
                    key={team.id}
                    className={`${styles.teamChoice} ${team.id === selectedTeamId ? styles.activeTeamChoice : ''}`}
                    onClick={() => selectTeam(team.id)}
                    aria-current={team.id === selectedTeamId ? 'true' : undefined}
                  >
                    <TeamAvatar team={team} />
                    <span>{team.team_name}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </section>
      )}

              {selectedTeamData && (
                <div className={styles.body}>
                  <div className={styles.name_banner}>
                    <h1 className={styles.team_name}>{selectedTeamData.owner}'s Team</h1>
                  </div>
                  <section className={styles.capSummary} aria-labelledby="salary-cap-heading">
                    <p className={styles.capLabel} id="salary-cap-heading">Salary cap</p>
                    <div className={styles.capHeadline}>
                      <strong>{formatCap(usedCap)}</strong>
                      <span>of {formatCap(salaryCap)} used</span>
                    </div>
                    <div
                      className={styles.capTrack}
                      role="img"
                      aria-label={`${formatCap(activeCap)} active cap, ${formatCap(deadCap)} dead money, ${formatCap(remainingCap)} remaining`}
                    >
                      <span className={styles.activeCapBar} style={{ width: `${activeCapPercent}%` }} />
                      <span className={styles.deadCapBar} style={{ width: `${deadCapPercent}%` }} />
                    </div>
                    <div className={styles.capLegend}>
                      <span><i className={styles.activeDot} />Active <strong>{formatCap(activeCap)}</strong></span>
                      <span><i className={styles.deadDot} />Dead money <strong>{formatCap(deadCap)}</strong></span>
                      <span className={styles.remainingCap}>{formatCap(remainingCap)} remaining</span>
                    </div>
                  </section>
                    <div className={styles.filterBar} aria-label="Roster filters">
                      <label>
                        <span>Position</span>
                        <select value={positionFilter} onChange={(event) => setPositionFilter(event.target.value)}>
                          <option value="all">Any</option>
                          {positionOptions.map(position => <option value={position} key={position}>{position}</option>)}
                        </select>
                      </label>
                      <label>
                        <span>Contract amount</span>
                        <select
                          value={contractAmountFilter}
                          onChange={(event) => setContractAmountFilter(event.target.value as ContractAmountFilter)}
                        >
                          <option value="all">Any</option>
                          <option value="zero">$0</option>
                          <option value="1-5">$1–$5</option>
                          <option value="6-10">$6–$10</option>
                          <option value="11-20">$11–$20</option>
                          <option value="21-plus">$21+</option>
                        </select>
                      </label>
                      <label>
                        <span>Duration</span>
                        <select value={durationFilter} onChange={(event) => setDurationFilter(event.target.value)}>
                          <option value="all">Any</option>
                          {durationOptions.map(duration => (
                            <option value={duration} key={duration}>{duration} {duration === 1 ? 'year' : 'years'}</option>
                          ))}
                        </select>
                      </label>
                      <label>
                        <span>Type</span>
                        <select value={contractTypeFilter} onChange={(event) => setContractTypeFilter(event.target.value)}>
                          <option value="all">Any</option>
                          <option value="full">Full</option>
                          <option value="partial">Partial</option>
                          <option value="none">None</option>
                          <option value="dead-money">Terminated</option>
                        </select>
                      </label>
                      <button
                        type="button"
                        className={styles.clearFilters}
                        disabled={!hasActiveFilters}
                        onClick={resetFilters}
                      >
                        Clear
                      </button>
                    </div>
                    <div className={styles.rosterSheet}>
                      <div className={styles.sheetHeaders}>
                        <strong className={styles.playerHeader}>Player</strong>
                        <strong className={styles.contractHeader}>Contract</strong>
                      </div>
                      <div className={styles.columnHeaders} aria-hidden="true">
                        <span />
                        <span>Name</span>
                        <span>Pos</span>
                        <span>Num</span>
                        <span>Team</span>
                        <span />
                        <span>Amount</span>
                        <span>Duration</span>
                        <span>Type</span>
                      </div>
                      {rosterSections.filter(section => section.players.length).map(section => (
                        <section className={styles.rosterGroup} aria-labelledby={`${section.id}-heading`} key={section.id}>
                          <h2 className={styles.section_header} id={`${section.id}-heading`}>{section.title}</h2>
                          {renderRosterSection(section.players)}
                        </section>
                      ))}
                      {deadMoneyPlayers.length > 0 && (
                        <section className={styles.rosterGroup} aria-labelledby="dead-money-heading">
                          <h2 className={styles.section_header} id="dead-money-heading">Dead money</h2>
                          {renderDeadMoneySection(deadMoneyPlayers)}
                        </section>
                      )}
                      {!rosterSections.some(section => section.players.length) && deadMoneyPlayers.length === 0 && (
                        <p className={styles.emptyState}>No players match these filters.</p>
                      )}
                    </div>
                </div>
              )}
      <div className={styles.bottom_spacer}></div>
    </div>
  );
};

export default TeamRosters;
